import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    Button, Card, Col, DatePicker, Row, Select, Space, Table,
    Tag, Typography, Alert, Breadcrumb, Statistic, Tooltip,
    Grid, Badge, Spin, Avatar,
} from 'antd';
import {
    ArrowLeftOutlined, DownloadOutlined, ReloadOutlined, ShopOutlined,
    CalendarOutlined, FilterOutlined, CompassOutlined, EnvironmentOutlined,
    EditOutlined
} from '@ant-design/icons';
import MainLayout from '../../components/MainLayout';
import { franchiseService } from '../../services/franchiseService';
import { errorMsg } from '../../utils/ShowMessage';
import { useNavigate, useParams, Link } from 'react-router-dom';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;
const { Option } = Select;

const ORDER_STATUS_COLORS = {
    PENDING: 'orange',
    WAITING_FOR_DRIVER_ACCEPT: 'gold',
    CONFIRMED: 'blue',
    PROCESSING: 'cyan',
    READY_FOR_PICKUP: 'geekblue',
    IN_TRANSIT: 'purple',
    OUT_FOR_DELIVERY: 'blue',
    DELIVERED: 'green',
    CANCELLED: 'red',
    FAILED: 'red',
};

// ─── CSV Export ───────────────────────────────────────────────────────────
function escapeCSV(val) {
    if (val === null || val === undefined) return '';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

function exportToCSV(franchiseName, orders) {
    const headers = ['Order ID', 'Customer Name', 'Customer Phone', 'Delivery Address', 'Pincode', 'Amount', 'Payment Type', 'Driver', 'Status', 'Date'];
    const rows = orders.map(o => [
        o.orderId,
        o.customerName,
        o.customerPhone,
        o.deliveryAddress,
        o.zipcode,
        o.totalPrice,
        o.paymentType,
        o.driverName || '',
        o.orderStatus,
        o.createdAt ? new Date(o.createdAt).toLocaleDateString() : '',
    ]);

    const csv = [headers, ...rows].map(row => row.map(escapeCSV).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${franchiseName?.replace(/\s+/g, '_') || 'franchise'}_orders_${dayjs().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// ─── Main Detail Page ─────────────────────────────────────────────────────
export default function FranchiseDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const screens = useBreakpoint();

    const [loading, setLoading] = useState(false);
    const [franchise, setFranchise] = useState(null);
    const [orders, setOrders] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });
    const [error, setError] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [stats, setStats] = useState(null);
    const allOrdersForExport = useRef([]);

    const fetchOrders = useCallback(async (page = 1, newDateRange = dateRange, newStatus = statusFilter) => {
        if (!id) return;
        setLoading(true);
        setError(null);
        try {
            const params = { page, limit: 20 };
            if (newDateRange?.[0]) params.from = dayjs(newDateRange[0]).format('YYYY-MM-DD');
            if (newDateRange?.[1]) params.to = dayjs(newDateRange[1]).format('YYYY-MM-DD');
            if (newStatus) params.status = newStatus;

            const res = await franchiseService.getOrders(id, params);
            if (res?.success) {
                setOrders(res.data || []);
                setPagination(res.pagination || { total: 0, page: 1, limit: 20, totalPages: 0 });
                if (res.franchise && !franchise) setFranchise(res.franchise);
            } else {
                setError(res?.message || 'Failed to load orders');
            }
        } catch (e) {
            setError(e?.message || 'Network error');
        } finally {
            setLoading(false);
        }
    }, [id, dateRange, statusFilter, franchise]);

    const fetchFranchiseInfo = useCallback(async () => {
        try {
            const res = await franchiseService.getById(id);
            if (res?.success) setFranchise(res.data);
        } catch (e) { /* silent */ }
    }, [id]);

    const fetchStats = useCallback(async () => {
        try {
            const params = {};
            if (dateRange?.[0]) params.from = dayjs(dateRange[0]).format('YYYY-MM-DD');
            if (dateRange?.[1]) params.to = dayjs(dateRange[1]).format('YYYY-MM-DD');
            const res = await franchiseService.getStats(id, params);
            if (res?.success) setStats(res.data?.summary || null);
        } catch (e) { /* silent */ }
    }, [id, dateRange]);

    useEffect(() => {
        fetchFranchiseInfo();
        fetchOrders(1, null, null);
        fetchStats();
    }, [id]);

    const handleDateChange = (range) => {
        setDateRange(range);
        fetchOrders(1, range, statusFilter);
        // Re-fetch stats too
        if (id) {
            const params = {};
            if (range?.[0]) params.from = dayjs(range[0]).format('YYYY-MM-DD');
            if (range?.[1]) params.to = dayjs(range[1]).format('YYYY-MM-DD');
            franchiseService.getStats(id, params).then(res => {
                if (res?.success) setStats(res.data?.summary || null);
            });
        }
    };

    const handleStatusChange = (val) => {
        setStatusFilter(val || null);
        fetchOrders(1, dateRange, val || null);
    };

    const handleTableChange = (paginationInfo) => {
        fetchOrders(paginationInfo.current, dateRange, statusFilter);
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            // Fetch all pages for export
            const params = { page: 1, limit: 1000 };
            if (dateRange?.[0]) params.from = dayjs(dateRange[0]).format('YYYY-MM-DD');
            if (dateRange?.[1]) params.to = dayjs(dateRange[1]).format('YYYY-MM-DD');
            if (statusFilter) params.status = statusFilter;

            const res = await franchiseService.getOrders(id, params);
            if (res?.success) {
                exportToCSV(franchise?.name, res.data || []);
            } else {
                errorMsg(res?.message || 'Failed to export');
            }
        } catch (e) {
            errorMsg(e?.message || 'Export failed');
        } finally {
            setExporting(false);
        }
    };

    const columns = [
        {
            title: 'Order ID',
            dataIndex: 'orderId',
            key: 'orderId',
            width: 120,
            render: v => <Text code style={{ fontSize: 12 }}>{v}</Text>,
        },
        {
            title: 'Customer',
            key: 'customer',
            width: 160,
            render: (_, r) => (
                <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{r.customerName || '—'}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{r.customerPhone || ''}</div>
                </div>
            ),
        },
        {
            title: 'Delivery Address',
            dataIndex: 'deliveryAddress',
            key: 'deliveryAddress',
            ellipsis: true,
            render: (v, r) => (
                <Tooltip title={v}>
                    <div style={{ fontSize: 12 }}>
                        {v || '—'}
                        {r.zipcode && <Tag style={{ marginLeft: 4, fontSize: 10 }} color="blue">{r.zipcode}</Tag>}
                    </div>
                </Tooltip>
            ),
        },
        {
            title: 'Amount',
            dataIndex: 'totalPrice',
            key: 'totalPrice',
            width: 100,
            render: v => <Text style={{ fontWeight: 600 }}>₹{parseFloat(v || 0).toFixed(2)}</Text>,
        },
        {
            title: 'Driver',
            key: 'driver',
            width: 130,
            render: (_, r) => r.driverName
                ? <Text style={{ fontSize: 12 }}>{r.driverName}</Text>
                : <Text type="secondary" style={{ fontSize: 12 }}>Unassigned</Text>,
        },
        {
            title: 'Status',
            dataIndex: 'orderStatus',
            key: 'orderStatus',
            width: 140,
            render: status => (
                <Tag color={ORDER_STATUS_COLORS[status] || 'default'} style={{ fontSize: 11 }}>
                    {status?.replace(/_/g, ' ') || '—'}
                </Tag>
            ),
        },
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 110,
            render: v => v ? <span style={{ fontSize: 12 }}>{new Date(v).toLocaleDateString()}</span> : '—',
        },
    ];

    const isMobile = !screens.md;

    return (
        <MainLayout>
            <div style={{ width: '100%' }}>
                {/* Breadcrumb */}
                <Breadcrumb
                    style={{ marginBottom: 16 }}
                    items={[
                        { title: <Link to="/franchise">Franchises</Link> },
                        { title: franchise?.name || 'Franchise Details' },
                    ]}
                />

                {/* Header */}
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                    flexWrap: 'wrap', gap: 12, marginBottom: 20,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate('/franchise')}
                            style={{ borderRadius: 8 }}
                        />
                        <div>
                            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 10 }}>
                                {franchise?.logo ? (
                                    <Avatar
                                        src={franchise.logo}
                                        size={36}
                                        shape="square"
                                        style={{ borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff' }}
                                    />
                                ) : (
                                    <ShopOutlined style={{ color: '#2563eb' }} />
                                )}
                                {franchise?.name || 'Franchise Orders'}
                            </h2>
                            {franchise?.city && (
                                <div style={{ fontSize: 13, color: '#64748b', marginTop: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                                    <span>{franchise.address ? `${franchise.address}, ` : ''}{franchise.city}{franchise.state ? `, ${franchise.state}` : ''}</span>
                                    <span>·</span>
                                    {franchise.radiusEnabled ? (
                                        <Tag color="blue" icon={<CompassOutlined />}>
                                            {franchise.deliveryRadiusKm || 10} km Radius Active
                                        </Tag>
                                    ) : (
                                        <Tag color="default">
                                            Radius Off ({franchise.deliveryRadiusKm || 10} km)
                                        </Tag>
                                    )}
                                    <span>·</span>
                                    {franchise.location_id && (
                                        <>
                                            <Tag color="purple" style={{ fontSize: 11, fontWeight: 600 }}>
                                                Location ID: {franchise.location_id}
                                            </Tag>
                                            <span>·</span>
                                        </>
                                    )}
                                    <Tag color="geekblue" style={{ fontSize: 11, fontWeight: 600 }}>
                                        Pincode: {(franchise.pincodes && franchise.pincodes.length > 0) ? franchise.pincodes[0] : (franchise.pincode || '—')}
                                    </Tag>
                                </div>
                            )}
                        </div>
                    </div>
                    <Space wrap>
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => navigate(`/franchise/edit/${id}`)}
                            style={{ borderRadius: 8 }}
                        >
                            Edit Franchise
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => { fetchOrders(1); fetchStats(); }}
                            loading={loading}
                            style={{ borderRadius: 8 }}
                        >
                            Refresh
                        </Button>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={handleExport}
                            loading={exporting}
                            style={{ borderRadius: 8, borderColor: '#2563eb', color: '#2563eb' }}
                        >
                            Export CSV
                        </Button>
                    </Space>
                </div>

                {/* Stats Cards */}
                {stats && (
                    <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
                        {[
                            { title: 'Total Orders', value: stats.totalOrders || 0, color: '#0369a1', bg: '#f0f9ff' },
                            { title: 'Pending', value: stats.pending || 0, color: '#a16207', bg: '#fefce8' },
                            { title: 'Out for Delivery', value: stats.outForDelivery || 0, color: '#1d4ed8', bg: '#eff6ff' },
                            { title: 'Delivered', value: stats.delivered || 0, color: '#15803d', bg: '#f0fdf4' },
                            { title: 'Cancelled', value: stats.cancelled || 0, color: '#b91c1c', bg: '#fef2f2' },
                        ].map(s => (
                            <Col xs={12} sm={8} md={5} key={s.title}>
                                <Card style={{ borderRadius: 10, background: s.bg, border: '1px solid #e2e8f0' }}>
                                    <Statistic
                                        title={<span style={{ fontSize: 11, color: '#64748b' }}>{s.title}</span>}
                                        value={s.value}
                                        valueStyle={{ fontSize: 22, fontWeight: 700, color: s.color }}
                                    />
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}

                {/* Filters */}
                <Card style={{ borderRadius: 12, marginBottom: 16, border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                        <FilterOutlined style={{ color: '#64748b' }} />
                        <Text style={{ fontWeight: 500 }}>Filters:</Text>
                        <RangePicker
                            onChange={handleDateChange}
                            value={dateRange}
                            allowClear
                            style={{ borderRadius: 8, maxWidth: isMobile ? '100%' : 280 }}
                            placeholder={['From date', 'To date']}
                            disabledDate={d => d && d > dayjs().endOf('day')}
                        />
                        <Select
                            placeholder="Filter by status"
                            allowClear
                            value={statusFilter}
                            onChange={handleStatusChange}
                            style={{ width: isMobile ? '100%' : 200, borderRadius: 8 }}
                        >
                            {Object.keys(ORDER_STATUS_COLORS).map(s => (
                                <Option key={s} value={s}>{s.replace(/_/g, ' ')}</Option>
                            ))}
                        </Select>
                        {(dateRange || statusFilter) && (
                            <Button
                                size="small"
                                onClick={() => { setDateRange(null); setStatusFilter(null); fetchOrders(1, null, null); fetchStats(); }}
                                style={{ borderRadius: 6 }}
                            >
                                Clear filters
                            </Button>
                        )}
                    </div>
                </Card>

                {/* Error */}
                {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} closable onClose={() => setError(null)} />}

                {/* Orders Table */}
                <Card style={{ borderRadius: 12, border: '1px solid #e2e8f0' }} bodyStyle={{ padding: isMobile ? 8 : 16 }}>
                    <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <Text strong style={{ fontSize: 15 }}>
                            Orders {pagination.total > 0 && <Text type="secondary" style={{ fontWeight: 400 }}>({pagination.total} total)</Text>}
                        </Text>
                        {dateRange && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <CalendarOutlined /> {dayjs(dateRange[0]).format('MMM D')} – {dayjs(dateRange[1]).format('MMM D, YYYY')}
                            </Text>
                        )}
                    </div>
                    <Table
                        dataSource={orders}
                        columns={isMobile ? columns.filter(c => ['orderId', 'customer', 'orderStatus', 'createdAt'].includes(c.key)) : columns}
                        rowKey="id"
                        loading={loading}
                        scroll={{ x: isMobile ? 400 : 900 }}
                        size={isMobile ? 'small' : 'middle'}
                        pagination={{
                            current: pagination.page,
                            pageSize: pagination.limit,
                            total: pagination.total,
                            showSizeChanger: false,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        onChange={handleTableChange}
                        locale={{ emptyText: <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>No orders found for the selected filters</div> }}
                    />
                </Card>
            </div>
        </MainLayout>
    );
}

